import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export default function PrivacyPage() {
  const { t, i18n } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const isRTL = i18n.language === 'ar';

  useEffect(() => {
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [isRTL, i18n.language]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
  };

  return (
    <div className="bg-white min-h-screen">
      {/* Header */}
      <header className={`sticky top-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-sm' : 'bg-white'}`}>
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <a href="/" className="flex items-center cursor-pointer">
              <img src="https://5vix.com/logo.png" alt="5vix Logo" className="h-10 w-10" />
            </a>
          </div>
          
          <nav className="hidden md:flex items-center space-x-8 rtl:space-x-reverse">
            <a href="/#home" className="text-gray-800 hover:text-[#673ab7] transition-colors">{t('nav.home')}</a>
            <a href="/#services" className="text-gray-800 hover:text-[#673ab7] transition-colors">{t('nav.services')}</a>
            <a href="/#register" className="text-gray-800 hover:text-[#673ab7] transition-colors">{t('nav.register')}</a>
            <a href="/#blog" className="text-gray-800 hover:text-[#673ab7] transition-colors">{t('nav.blog')}</a>
          </nav>

          <div className="flex items-center gap-4">
            <button 
              onClick={() => changeLanguage(i18n.language === 'en' ? 'ar' : 'en')}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all whitespace-nowrap cursor-pointer font-medium"
            >
              {i18n.language === 'en' ? 'ع' : 'EN'}
            </button>
            <a href="/#register" className="bg-[#673ab7] text-white px-6 py-2 rounded-lg hover:bg-[#5e35b1] transition-all whitespace-nowrap cursor-pointer">
              {t('nav.tryFree')}
            </a>
            <button onClick={toggleMobileMenu} className="md:hidden w-10 h-10 flex items-center justify-center text-gray-700 cursor-pointer">
              <i className="ri-menu-line text-xl"></i>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={toggleMobileMenu}></div>
          <div className={`absolute ${isRTL ? 'left-0' : 'right-0'} top-0 w-64 h-full bg-white shadow-lg p-6`}>
            <button onClick={toggleMobileMenu} className="mb-6 text-gray-700 font-medium cursor-pointer">Close</button>
            <nav className="flex flex-col space-y-4">
              <a href="/#home" className="text-gray-800 hover:text-[#673ab7]" onClick={toggleMobileMenu}>{t('nav.home')}</a>
              <a href="/#services" className="text-gray-800 hover:text-[#673ab7]" onClick={toggleMobileMenu}>{t('nav.services')}</a>
              <a href="/#register" className="text-gray-800 hover:text-[#673ab7]" onClick={toggleMobileMenu}>{t('nav.register')}</a>
              <a href="/#blog" className="text-gray-800 hover:text-[#673ab7]" onClick={toggleMobileMenu}>{t('nav.blog')}</a>
            </nav>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#673ab7]/10 to-[#673ab7]/5 py-16">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto">
            <div className="w-16 h-16 bg-[#673ab7] rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-shield-check-line text-white text-3xl"></i>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
              {t('privacy.title')}
            </h1>
            <p className="text-lg text-gray-700">
              {t('privacy.lastUpdated')}
            </p>
          </div>
        </div>
      </section>

      {/* Privacy Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Introduction */}
            <div className="mb-12">
              <p className="text-gray-700 leading-relaxed text-base">
                {t('privacy.introduction')}
              </p>
            </div>

            {/* Information We Collect */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold mb-6 text-gray-900 flex items-center">
                <div className="w-8 h-8 bg-[#673ab7]/10 rounded-lg flex items-center justify-center mr-3 rtl:mr-0 rtl:ml-3">
                  <i className="ri-file-list-line text-[#673ab7]"></i>
                </div>
                {t('privacy.section1.title')}
              </h2>
              <div className="space-y-4 pl-11 rtl:pl-0 rtl:pr-11">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">{t('privacy.section1.subtitle1')}</h3>
                  <p className="text-gray-700 leading-relaxed text-base">{t('privacy.section1.content1')}</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">{t('privacy.section1.subtitle2')}</h3>
                  <p className="text-gray-700 leading-relaxed text-base">{t('privacy.section1.content2')}</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">{t('privacy.section1.subtitle3')}</h3>
                  <p className="text-gray-700 leading-relaxed text-base">{t('privacy.section1.content3')}</p>
                </div>
              </div>
            </div>

            {/* How We Use Your Information */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold mb-6 text-gray-900 flex items-center">
                <div className="w-8 h-8 bg-[#673ab7]/10 rounded-lg flex items-center justify-center mr-3 rtl:mr-0 rtl:ml-3">
                  <i className="ri-settings-line text-[#673ab7]"></i>
                </div>
                {t('privacy.section2.title')}
              </h2>
              <div className="pl-11 rtl:pl-0 rtl:pr-11">
                <p className="text-gray-700 leading-relaxed mb-4 text-base">{t('privacy.section2.content')}</p>
                <ul className="space-y-2">
                  <li className="flex items-start text-gray-700">
                    <div className="w-5 h-5 flex items-center justify-center mr-2 rtl:mr-0 rtl:ml-2 mt-0.5">
                      <i className="ri-check-line text-[#673ab7]"></i>
                    </div>
                    <span className="text-base">{t('privacy.section2.item1')}</span>
                  </li>
                  <li className="flex items-start text-gray-700">
                    <div className="w-5 h-5 flex items-center justify-center mr-2 rtl:mr-0 rtl:ml-2 mt-0.5">
                      <i className="ri-check-line text-[#673ab7]"></i>
                    </div>
                    <span className="text-base">{t('privacy.section2.item2')}</span>
                  </li>
                  <li className="flex items-start text-gray-700">
                    <div className="w-5 h-5 flex items-center justify-center mr-2 rtl:mr-0 rtl:ml-2 mt-0.5">
                      <i className="ri-check-line text-[#673ab7]"></i>
                    </div>
                    <span className="text-base">{t('privacy.section2.item3')}</span>
                  </li>
                  <li className="flex items-start text-gray-700">
                    <div className="w-5 h-5 flex items-center justify-center mr-2 rtl:mr-0 rtl:ml-2 mt-0.5">
                      <i className="ri-check-line text-[#673ab7]"></i>
                    </div>
                    <span className="text-base">{t('privacy.section2.item4')}</span>
                  </li>
                  <li className="flex items-start text-gray-700">
                    <div className="w-5 h-5 flex items-center justify-center mr-2 rtl:mr-0 rtl:ml-2 mt-0.5">
                      <i className="ri-check-line text-[#673ab7]"></i>
                    </div>
                    <span className="text-base">{t('privacy.section2.item5')}</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Information Sharing */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold mb-6 text-gray-900 flex items-center">
                <div className="w-8 h-8 bg-[#673ab7]/10 rounded-lg flex items-center justify-center mr-3 rtl:mr-0 rtl:ml-3">
                  <i className="ri-share-line text-[#673ab7]"></i>
                </div>
                {t('privacy.section3.title')}
              </h2>
              <div className="pl-11 rtl:pl-0 rtl:pr-11">
                <p className="text-gray-700 leading-relaxed text-base">{t('privacy.section3.content')}</p>
              </div>
            </div>

            {/* Data Security */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold mb-6 text-gray-900 flex items-center">
                <div className="w-8 h-8 bg-[#673ab7]/10 rounded-lg flex items-center justify-center mr-3 rtl:mr-0 rtl:ml-3">
                  <i className="ri-lock-line text-[#673ab7]"></i>
                </div>
                {t('privacy.section4.title')}
              </h2>
              <div className="pl-11 rtl:pl-0 rtl:pr-11">
                <p className="text-gray-700 leading-relaxed text-base">{t('privacy.section4.content')}</p>
              </div>
            </div>

            {/* Cookies and Tracking */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold mb-6 text-gray-900 flex items-center">
                <div className="w-8 h-8 bg-[#673ab7]/10 rounded-lg flex items-center justify-center mr-3 rtl:mr-0 rtl:ml-3">
                  <i className="ri-cookie-line text-[#673ab7]"></i>
                </div>
                {t('privacy.section5.title')}
              </h2>
              <div className="pl-11 rtl:pl-0 rtl:pr-11">
                <p className="text-gray-700 leading-relaxed text-base">{t('privacy.section5.content')}</p>
              </div>
            </div>

            {/* Your Rights */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold mb-6 text-gray-900 flex items-center">
                <div className="w-8 h-8 bg-[#673ab7]/10 rounded-lg flex items-center justify-center mr-3 rtl:mr-0 rtl:ml-3">
                  <i className="ri-user-settings-line text-[#673ab7]"></i>
                </div>
                {t('privacy.section6.title')}
              </h2>
              <div className="pl-11 rtl:pl-0 rtl:pr-11">
                <p className="text-gray-700 leading-relaxed mb-4 text-base">{t('privacy.section6.content')}</p>
                <ul className="space-y-2">
                  <li className="flex items-start text-gray-700">
                    <div className="w-5 h-5 flex items-center justify-center mr-2 rtl:mr-0 rtl:ml-2 mt-0.5">
                      <i className="ri-check-line text-[#673ab7]"></i>
                    </div>
                    <span className="text-base">{t('privacy.section6.item1')}</span>
                  </li>
                  <li className="flex items-start text-gray-700">
                    <div className="w-5 h-5 flex items-center justify-center mr-2 rtl:mr-0 rtl:ml-2 mt-0.5">
                      <i className="ri-check-line text-[#673ab7]"></i>
                    </div>
                    <span className="text-base">{t('privacy.section6.item2')}</span>
                  </li>
                  <li className="flex items-start text-gray-700">
                    <div className="w-5 h-5 flex items-center justify-center mr-2 rtl:mr-0 rtl:ml-2 mt-0.5">
                      <i className="ri-check-line text-[#673ab7]"></i>
                    </div>
                    <span className="text-base">{t('privacy.section6.item3')}</span>
                  </li>
                  <li className="flex items-start text-gray-700">
                    <div className="w-5 h-5 flex items-center justify-center mr-2 rtl:mr-0 rtl:ml-2 mt-0.5">
                      <i className="ri-check-line text-[#673ab7]"></i>
                    </div>
                    <span className="text-base">{t('privacy.section6.item4')}</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Third-Party Links */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold mb-6 text-gray-900 flex items-center">
                <div className="w-8 h-8 bg-[#673ab7]/10 rounded-lg flex items-center justify-center mr-3 rtl:mr-0 rtl:ml-3">
                  <i className="ri-links-line text-[#673ab7]"></i>
                </div>
                {t('privacy.section7.title')}
              </h2>
              <div className="pl-11 rtl:pl-0 rtl:pr-11">
                <p className="text-gray-700 leading-relaxed text-base">{t('privacy.section7.content')}</p>
              </div>
            </div>

            {/* Changes to Privacy Policy */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold mb-6 text-gray-900 flex items-center">
                <div className="w-8 h-8 bg-[#673ab7]/10 rounded-lg flex items-center justify-center mr-3 rtl:mr-0 rtl:ml-3">
                  <i className="ri-refresh-line text-[#673ab7]"></i>
                </div>
                {t('privacy.section8.title')}
              </h2>
              <div className="pl-11 rtl:pl-0 rtl:pr-11">
                <p className="text-gray-700 leading-relaxed text-base">{t('privacy.section8.content')}</p>
              </div>
            </div>

            {/* Contact Us */}
            <div className="bg-gradient-to-br from-[#673ab7]/5 to-[#673ab7]/10 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-4 text-gray-900 flex items-center">
                <div className="w-8 h-8 bg-[#673ab7] rounded-lg flex items-center justify-center mr-3 rtl:mr-0 rtl:ml-3">
                  <i className="ri-mail-line text-white"></i>
                </div>
                {t('privacy.section9.title')}
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6 text-base">{t('privacy.section9.content')}</p>
              <div className="space-y-3">
                <div className="flex items-center text-gray-700">
                  <div className="w-5 h-5 flex items-center justify-center mr-3 rtl:mr-0 rtl:ml-3">
                    <i className="ri-mail-line text-[#673ab7]"></i>
                  </div>
                  <a href="mailto:info@5vix.com" className="hover:text-[#673ab7] transition-colors">info@5vix.com</a>
                </div>
                <div className="flex items-center text-gray-700">
                  <div className="w-5 h-5 flex items-center justify-center mr-3 rtl:mr-0 rtl:ml-3">
                    <i className="ri-phone-line text-[#673ab7]"></i>
                  </div>
                  <a href="tel:201044553254" className="hover:text-[#673ab7] transition-colors">01044553254</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-[#673ab7] to-[#5e35b1]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center text-white">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              {t('privacy.cta.title')}
            </h2>
            <p className="text-lg text-white/90 mb-8">
              {t('privacy.cta.subtitle')}
            </p>
            <a href="/#register" className="bg-white text-[#673ab7] px-8 py-3 rounded-lg hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer inline-block">
              {t('privacy.cta.button')}
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white pt-16 pb-8">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-12">
            <div>
              <a href="/" className="text-3xl font-bold text-white mb-4 block cursor-pointer">5vix</a>
              <p className="text-gray-400 mb-6">
                {t('footer.description')}
              </p>
              <div className="flex flex-col space-y-3 mb-6">
                <div className="flex items-center text-gray-400">
                  <div className="w-5 h-5 flex items-center justify-center mr-2 rtl:mr-0 rtl:ml-2">
                    <i className="ri-mail-line"></i>
                  </div>
                  <a href="mailto:info@5vix.com" className="hover:text-white transition-colors">info@5vix.com</a>
                </div>
                <div className="flex items-center text-gray-400">
                  <div className="w-5 h-5 flex items-center justify-center mr-2 rtl:mr-0 rtl:ml-2">
                    <i className="ri-phone-line"></i>
                  </div>
                  <a href="tel:201044553254" className="hover:text-white transition-colors">01044553254</a>
                </div>
              </div>
              <div className="flex space-x-4 rtl:space-x-reverse">
                <a href="https://www.tiktok.com/@5vixofficial" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center bg-gray-800 rounded-full hover:bg-pink-500 transition-colors cursor-pointer">
                  <i className="ri-tiktok-fill text-white text-xl"></i>
                </a>
                <a href="https://www.youtube.com/@5vix-c3p" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center bg-gray-800 rounded-full hover:bg-red-600 transition-colors cursor-pointer">
                  <i className="ri-youtube-fill text-white text-xl"></i>
                </a>
                <a href="https://www.instagram.com/5vixofficial/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center bg-gray-800 rounded-full hover:bg-pink-600 transition-colors cursor-pointer">
                  <i className="ri-instagram-fill text-white text-xl"></i>
                </a>
                <a href="https://www.linkedin.com/in/5vix-software-12058b363/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center bg-gray-800 rounded-full hover:bg-blue-600 transition-colors cursor-pointer">
                  <i className="ri-linkedin-fill text-white text-xl"></i>
                </a>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-6">{t('footer.quickLinks')}</h3>
              <ul className="space-y-3">
                <li><a href="/#home" className="text-gray-400 hover:text-white transition-colors cursor-pointer">{t('nav.home')}</a></li>
                <li><a href="/#services" className="text-gray-400 hover:text-white transition-colors cursor-pointer">{t('nav.services')}</a></li>
                <li><a href="/#register" className="text-gray-400 hover:text-white transition-colors cursor-pointer">{t('nav.register')}</a></li>
                <li><a href="/#blog" className="text-gray-400 hover:text-white transition-colors cursor-pointer">{t('nav.blog')}</a></li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-6">{t('footer.support')}</h3>
              <ul className="space-y-3">
                <li><a href="/help-center" className="text-gray-400 hover:text-white transition-colors cursor-pointer">{t('footer.helpCenter')}</a></li>
                <li><a href="/help-center" className="text-gray-400 hover:text-white transition-colors cursor-pointer">{t('footer.faq')}</a></li>
                <li><a href="tel:201044553254" className="text-gray-400 hover:text-white transition-colors cursor-pointer">{t('footer.contactUs')}</a></li>
                <li><a href="/terms" className="text-gray-400 hover:text-white transition-colors cursor-pointer">{t('footer.terms')}</a></li>
                <li><a href="/privacy" className="text-gray-400 hover:text-white transition-colors cursor-pointer">{t('footer.privacy')}</a></li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-6">{t('footer.newsletter')}</h3>
              <p className="text-gray-400 mb-4">{t('footer.newsletterDesc')}</p>
              <form className="mb-4">
                <div className="flex">
                  <input
                    type="email"
                    placeholder={t('register.email')}
                    className="bg-gray-800 text-white px-4 py-2 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-[#673ab7] flex-1"
                  />
                  <button className="bg-[#673ab7] text-white px-4 py-2 rounded-r-lg hover:bg-[#5e35b1] transition-all whitespace-nowrap cursor-pointer">
                    {t('footer.subscribe')}
                  </button>
                </div>
              </form>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-gray-400 mb-4 md:mb-0">{t('footer.copyright')}</p>
              <div className="flex space-x-4 rtl:space-x-reverse">
                <a href="/terms" className="text-gray-400 hover:text-white transition-colors cursor-pointer">{t('footer.terms')}</a>
                <a href="/privacy" className="text-gray-400 hover:text-white transition-colors cursor-pointer">{t('footer.privacy')}</a>
                <a href="/privacy" className="text-gray-400 hover:text-white transition-colors cursor-pointer">{t('footer.cookies')}</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
